import { IS_AWS_ENV } from "constants/app.constants";
import {
  AwsWellStatus,
  EvaChart,
  WELL_STATUS_DISPLAY_NAMES,
  WELL_STATUS_MAX_COLUMNS,
  WellStatus,
  WellStatusTimeScale,
  isAwsWellStatus,
  wellStatusCurrentOptions,
  wellStatusOverTimeOptions
} from "constants/charts.enums";
import { WellStatusChartRequestType } from "types";
import { removeCount } from "utils/removeCountFromTitle";

import { defaultWellStatusCurrentStatus } from "./wellStatusCurrent";
import {
  AWS_DEFAULT_OVER_TIME_STATUS,
  defaultWellStatusOverTimeStatus
} from "./wellStatusOverTime";

// Azure over-time labels a dashboard may have saved, by their AWS meaning. LIC maps
// to Permit Approved because the seeded system dashboard saves it.
const LEGACY_AZURE_TO_AWS_STATUS = new Map<string, AwsWellStatus>([
  [WellStatus.Spud, "Spud"],
  [WellStatus.Prod, "Active"],
  [WellStatus.Cmpltd, "Completed"],
  [WellStatus.Si, "Inactive"],
  [WellStatus.Abd, "Abandoned"],
  [WellStatus.Lic, AWS_DEFAULT_OVER_TIME_STATUS]
]);

/**
 * Picks the status for the ACTIVE Well Status chart, falling back to that chart's
 * default when the given status belongs to the other one.
 *
 * The chart entity's `request` survives a chart-type switch, so the status left on
 * it may be the previous chart's. On Azure the two vocabularies are **disjoint** —
 * over-time takes derived labels ("SPUD", "DUC"), current takes stage-prefixed
 * `header.status` values ("06. PROD") — so a carried-over value is not merely a
 * poor default: it matches no well, and the chart renders empty with no error on
 * any layer. (Observed: switching over-time → current sent "SPUD" to
 * GetCurrentStatusCounts, which resolved 18 998 wells and matched 0.) On AWS both
 * charts share one list, so this instead catches a label saved from Azure ("PROD").
 *
 * Both the request builder and the toolbar dropdown must apply this, or they
 * disagree about what is selected — hence one shared function rather than the
 * `?? default` each had, which only fired when the status was absent entirely.
 */
export function resolveWellStatusSelection(
  status: Maybe<string>,
  isCurrent: boolean
): string {
  if (isCurrent) {
    return wellStatusCurrentOptions().some((option) => option === status)
      ? status
      : defaultWellStatusCurrentStatus();
  }
  if (wellStatusOverTimeOptions().includes(status)) return status;
  const legacy =
    IS_AWS_ENV && status ? LEGACY_AZURE_TO_AWS_STATUS.get(status) : undefined;
  return legacy ?? defaultWellStatusOverTimeStatus();
}

/**
 * The human-readable name for a status wire value ("PROD" → "Producing"),
 * falling back to the value itself so an unmapped status from the data still
 * renders as something rather than blank.
 *
 * Also strips a stage prefix before looking up, so the bare form chart-svc emits
 * ("PROD", after it removes "06. ") resolves the same as the stored one.
 */
export function wellStatusDisplayName(status: Maybe<string>): string {
  if (!status) return "";
  const direct = WELL_STATUS_DISPLAY_NAMES[status];
  if (direct) return direct;
  const unprefixed = status.replace(/^\d+\.\s*/, "");
  return WELL_STATUS_DISPLAY_NAMES[unprefixed] ?? status;
}

/**
 * Whether a label is a well-status wire value this app knows, as opposed to some
 * other string.
 *
 * Exists because `wellStatusDisplayName` falls back to its input, which makes a
 * status name and a focus-bin name indistinguishable downstream: "Montney" comes
 * back as "Montney" and reads like a status that simply has no friendly name.
 * Callers that must tell the two apart — the All Statuses legend, whose series are
 * statuses while the map legend's are bins — need the question asked directly.
 */
export function isKnownWellStatusValue(status: Maybe<string>): boolean {
  if (!status) return false;
  if (WELL_STATUS_DISPLAY_NAMES[status]) return true;
  // AWS statuses are already display text, so they have no entry in the names map.
  // Checked only on AWS: "Active" or "Unknown" is just as often a focus-bin name.
  if (IS_AWS_ENV && isAwsWellStatus(status)) return true;
  return Boolean(WELL_STATUS_DISPLAY_NAMES[status.replace(/^\d+\.\s*/, "")]);
}

/**
 * Whether a response's series are keyed by STATUS rather than by focus bin — the
 * freshness test for All Statuses mode.
 *
 * `allStatuses` lives on the request, and the selector dispatches that the instant
 * the user picks it, with no response: the fetch has not returned. Until it does,
 * `apiResponse` still holds the previous filtered-mode payload of one series per
 * BIN. Anything that reshapes itself on the flag alone therefore describes data it
 * does not have yet.
 *
 * SHARED so the legend's items and its heading cannot answer it differently. They
 * are two halves of one control, and the first attempt guarded only the items —
 * which left the heading reading "Status" above "Montney / Duvernay" for the whole
 * fetch, the same bug arriving through the other half.
 *
 * `some`, not `every`: a status the display-name map has not been taught yet
 * should not drop the whole legend back to bins.
 */
export function isStatusKeyedResponse(
  series: Maybe<readonly { label?: Maybe<string> }[]>
): boolean {
  return (series ?? []).some((entry) => isKnownWellStatusValue(entry?.label));
}

/**
 * Reorders response series to follow the focus LEGEND's order.
 *
 * The aggregation ranks its groups by count (descending), while the legend the
 * widget shows is the map/focus legend, in the focus's own bin order. Left
 * alone the two disagree, and because the stack order, the tooltip rows and the
 * data-table columns all follow the SERIES order, everything except the legend
 * read in a different order from the legend (product: "datatable
 * and tooltip have headers in the wrong order, but the legend is in the right
 * order").
 *
 * The legend is the one the user can see and reorder, so it wins: series named
 * in `legendOrder` come first, in that order; anything the legend does not name
 * (a bin filtered out of the legend, or a stacked-mode STATUS series, which is
 * not a focus bin at all) keeps its original relative position at the end. That
 * fallback is what makes this safe to apply unconditionally — with an empty or
 * unmatched legend it is the identity function.
 */
export function orderSeriesByLegend<T extends { label?: Maybe<string> }>(
  series: readonly T[],
  legendOrder: readonly string[]
): T[] {
  if (!legendOrder.length || series.length < 2) return [...series];

  // Legend titles can carry a "(123)" well-count suffix that series labels never
  // do, so compare on the stripped form.
  const rank = new Map<string, number>();
  legendOrder.forEach((title, index) => {
    const key = removeCount(String(title ?? "")).trim();
    if (key && !rank.has(key)) rank.set(key, index);
  });

  const positionOf = (s: T) => rank.get(removeCount(String(s.label ?? "")).trim());

  return [...series].sort((a, b) => {
    const rankA = positionOf(a);
    const rankB = positionOf(b);
    // Unranked series sink below every ranked one but keep their own order,
    // which Array.prototype.sort guarantees (stable since ES2019).
    if (rankA === undefined && rankB === undefined) return 0;
    if (rankA === undefined) return 1;
    if (rankB === undefined) return -1;
    return rankA - rankB;
  });
}

// MIRROR `AWS_CUSTOM_DAILY_MAX_SPAN_DAYS` / `AWS_CUSTOM_WEEKLY_MAX_SPAN_DAYS` in
// eva-data-svc-rs/src/models/well_status_buckets.rs (Insights' ladder).
const AWS_CUSTOM_DAILY_MAX_SPAN_DAYS = 15;
const AWS_CUSTOM_WEEKLY_MAX_SPAN_DAYS = 105;

/**
 * Buckets a custom range is drawn at.
 *
 * MIRRORS the server's ladder (`custom_scale_for` in
 * eva-data-svc-rs/src/models/well_status_buckets.rs): the finest granularity
 * whose bucket count still fits {@link WELL_STATUS_MAX_COLUMNS}. Yearly is the
 * floor, so a range longer than that many years stays yearly and simply draws
 * more columns. AWS uses Insights' span ladder instead (`aws_custom_scale_for`).
 *
 * Duplicated in TS on purpose — the axis caption and the data-table header have
 * to name the granularity BEFORE the response lands, and the response's bucket
 * labels cannot distinguish weekly from daily (both format as "Jan 05, 2026").
 * The shared worked examples are pinned by tests on both sides.
 */
export function wellStatusCustomGranularity(
  from: Maybe<string>,
  to: Maybe<string>
): WellStatusTimeScale | undefined {
  if (!from || !to) return undefined;
  const start = new Date(from);
  const end = new Date(to);
  if (Number.isNaN(start.valueOf()) || Number.isNaN(end.valueOf()) || start > end) {
    return undefined;
  }

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  if (IS_AWS_ENV) {
    // Whole days between the dates, endpoints not both counted, and no monthly step.
    const span = Math.round((end.valueOf() - start.valueOf()) / MS_PER_DAY);
    if (span <= AWS_CUSTOM_DAILY_MAX_SPAN_DAYS) return WellStatusTimeScale.Daily;
    if (span <= AWS_CUSTOM_WEEKLY_MAX_SPAN_DAYS) return WellStatusTimeScale.Weekly;
    return WellStatusTimeScale.Yearly;
  }

  const days = Math.round((end.valueOf() - start.valueOf()) / MS_PER_DAY) + 1;
  const weeks = Math.ceil(days / 7);
  // Calendar months TOUCHED, counting both endpoints — i.e. how many monthly
  // columns the server will emit. Deliberately no day-of-month adjustment:
  // measuring elapsed whole months under-counts by one whenever the end day
  // precedes the start day, because a partial bucket is still drawn at each end.
  // 2026-01-31 → 2027-02-01 is 12 elapsed months but 14 columns, which slipped
  // past the 13-column cap. Mirrors months_spanned in well_status_buckets.rs.
  const months =
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    end.getUTCMonth() -
    start.getUTCMonth() +
    1;

  if (days <= WELL_STATUS_MAX_COLUMNS) return WellStatusTimeScale.Daily;
  if (weeks <= WELL_STATUS_MAX_COLUMNS) return WellStatusTimeScale.Weekly;
  if (months <= WELL_STATUS_MAX_COLUMNS) return WellStatusTimeScale.Monthly;
  return WellStatusTimeScale.Yearly;
}

/**
 * On-chart title for Status Over Time: "Well Status vs Time (Monthly)".
 *
 * Built client-side rather than taken from the response: the granularity is a
 * REQUEST property, and pairing it with a response the server produced for an
 * earlier time scale would caption the chart with the wrong word for the
 * in-flight moment between a scale change and its response.
 *
 * A Custom range has no granularity of its own — the server picks one to fit the
 * span — so the caption names what it RESOLVED to, e.g. "(Custom: Monthly)".
 */
export function wellStatusOverTimeChartTitle(
  timeScale: Maybe<string>,
  customRange?: Maybe<{ from?: Maybe<string>; to?: Maybe<string> }>
): string {
  let granularity: Maybe<string> = timeScale;
  if (timeScale === WellStatusTimeScale.Custom) {
    const resolved = wellStatusCustomGranularity(customRange?.from, customRange?.to);
    granularity = resolved ? `Custom: ${resolved}` : "Custom";
  }
  return granularity
    ? `${EvaChart.WellStatusOverTime} (${granularity})`
    : EvaChart.WellStatusOverTime;
}

type PresetRange = { from: string; to: string };

// Local getters on purpose: toISOString is UTC and would roll an evening in the
// Americas onto tomorrow.
const formatLocalDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * The window Novi Insights draws for a preset time scale: a line-for-line port of
 * its `getDefaultDateRange` / `alignToPreviousFriday` (ins_util.js), on the
 * viewer's local date and with plain JS `Date` month overflow (Jul 31 minus
 * 3 months is May 1, not Apr 30). That arithmetic is the point, so do not
 * "fix" it with a date library. Custom has no preset window.
 */
export function insightsPresetRange(
  timeScale: Maybe<string>,
  now: Date = new Date()
): Nullable<PresetRange> {
  let from: Date;
  switch (timeScale) {
    case WellStatusTimeScale.Daily:
      from = new Date(now);
      from.setDate(now.getDate() - 30);
      break;
    case WellStatusTimeScale.Weekly: {
      from = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      // Saturday-to-Friday weeks: step back to the Friday on or before.
      const day = from.getDay();
      from.setDate(from.getDate() - (day >= 5 ? day - 5 : day + 2));
      break;
    }
    case WellStatusTimeScale.Monthly:
      from = new Date(now.getFullYear() - 1, now.getMonth(), 1);
      break;
    case WellStatusTimeScale.Yearly:
      from = new Date(now.getFullYear() - 5, 0, 1);
      break;
    default:
      return null;
  }
  return { from: formatLocalDate(from), to: formatLocalDate(now) };
}

/**
 * On AWS an Over Time preset goes out as an explicit Insights window so the server
 * counts the same dates Insights does, on the viewer's date. Apply it to the
 * request at the wire only: written onto the chart entity it would go stale at
 * midnight and overwrite a saved Custom range. Azure, Custom and the current
 * chart come back as the same object. The window is computed at send time: an open
 * tab keeps its window until the next fetch, and an export recomputes from now.
 */
export function withAwsPresetRange(
  request: WellStatusChartRequestType,
  now?: Date
): WellStatusChartRequestType {
  if (!IS_AWS_ENV || request.chartType !== "WellStatusOverTime") return request;
  const customRange = insightsPresetRange(request.timeScale, now);
  return customRange ? { ...request, customRange } : request;
}

const DAY_OR_WEEK_LABEL = /^([A-Z][a-z]{2}) (\d{2}), \d{4}$/;

/**
 * Insights' bucket label for the chart axis, tooltip and on-screen table: "Oct 3"
 * for a day or a week's Friday. Month ("2026-10") and year labels already match.
 * Display only; the wire and exported labels stay "Oct 03, 2026". Dropping the
 * year cannot collide, since AWS day and week windows never exceed a few months.
 */
export function wellStatusBucketDisplayLabel(label: string): string {
  if (!IS_AWS_ENV) return label;
  const match = DAY_OR_WEEK_LABEL.exec(label);
  return match ? `${match[1]} ${Number(match[2])}` : label;
}
